import {
  Input,
  TextField,
  Label,
  Description,
  FieldError,
  type InputRootProps,
  type TextFieldRootProps,
} from "@heroui/react";
import { type AppInputVariant } from "./variants";

// ─── AppTextField ────────────────────────────────────────────────────────────

/**
 * Constrained text field props.
 * className escape hatch available for one-off overrides.
 */
export type AppTextFieldProps = Omit<
  TextFieldRootProps,
  "className" | "variant"
> & {
  /** Input visual variant (passed to child Input via context). @default "primary" */
  variant?: AppInputVariant;
  /** Optional className escape hatch for one-off overrides. */
  className?: string;
};

/**
 * AppTextField — Design system form field container.
 *
 * Wraps HeroUI v3 TextField. Passes `variant` to child AppInput via context.
 * Compose with AppInput, Label, Description, and FieldError.
 *
 * @example
 * ```tsx
 * <AppTextField name="email" type="email">
 *   <AppLabel>Email</AppLabel>
 *   <AppInput placeholder="you@example.com" />
 *   <AppDescription>We'll never share your email</AppDescription>
 * </AppTextField>
 * ```
 */
export function AppTextField({
  variant = "primary",
  className,
  ...props
}: AppTextFieldProps) {
  return <TextField {...props} variant={variant} className={className} />;
}

// ─── AppInput ────────────────────────────────────────────────────────────────

/**
 * Constrained input props.
 * className escape hatch available for one-off overrides.
 */
export type AppInputProps = Omit<InputRootProps, "className" | "variant"> & {
  /** Visual variant (overrides TextField context). */
  variant?: AppInputVariant;
  /** Optional className escape hatch for one-off overrides. */
  className?: string;
};

/**
 * AppInput — Design system text input.
 *
 * Wraps HeroUI v3 Input. Inherits variant from parent AppTextField context
 * unless explicitly overridden. Use inside AppTextField.
 */
export function AppInput({ variant, className, ...props }: AppInputProps) {
  return <Input {...props} variant={variant} className={className} />;
}

// ─── Re-exports ──────────────────────────────────────────────────────────────

/**
 * AppLabel — Direct re-export of HeroUI Label.
 * Used inside AppTextField for accessible form labels.
 */
export const AppLabel = Label;

/**
 * AppDescription — Direct re-export of HeroUI Description.
 * Used inside AppTextField for help text below the input.
 */
export const AppDescription = Description;

/**
 * AppFieldError — Direct re-export of HeroUI FieldError.
 * Used inside AppTextField to display validation error messages.
 */
export const AppFieldError = FieldError;
